import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import { movies } from "@/mocks/movies";
import Breadcrumb from "@/components/base/Breadcrumb";
import FilterPanel from "@/components/feature/FilterPanel";
import Pagination from "@/components/base/Pagination";
import Highlight from "@/components/base/Highlight";
import {
  emptyFilters,
  filterMovies,
  paginate,
  sortMovies,
  type FilterState,
} from "@/utils/media";

const PER_PAGE = 6;

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlKeyword = searchParams.get("q") || "";

  const [keyword, setKeyword] = useState(urlKeyword);
  const [filters, setFilters] = useState<FilterState>({ ...emptyFilters });
  const [page, setPage] = useState(1);

  useEffect(() => {
    setKeyword(urlKeyword);
    setFilters((f) => ({ ...f, keyword: urlKeyword }));
    setPage(1);
  }, [urlKeyword]);

  useSeo({
    title: urlKeyword
      ? `“${urlKeyword}”搜索结果 - 我的老婆姐姐`
      : "搜索影视剧 - 我的老婆姐姐",
    description: `我的老婆姐姐搜索页，支持按影视名称、演员、类型等关键词模糊匹配，快速找到想看的电视剧与电影。`,
    keywords: "影视搜索,剧集搜索,我的老婆姐姐,影视资讯",
  });

  const result = useMemo(() => {
    const activeFilters: FilterState = { ...filters, keyword: urlKeyword };
    const filtered = filterMovies(movies, activeFilters);
    const sorted = sortMovies(filtered, "hot");
    return paginate(sorted, page, PER_PAGE);
  }, [filters, urlKeyword, page]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    setSearchParams(q ? { q } : {});
  };

  const resetAll = () => {
    setKeyword("");
    setFilters({ ...emptyFilters });
    setSearchParams({});
    setPage(1);
  };

  const updateFilters = (next: FilterState) => {
    setFilters({ ...next, keyword: urlKeyword });
    setPage(1);
  };

  const recommendations = [...movies].sort((a, b) => b.views - a.views).slice(0, 4);

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb items={[{ label: "搜索" }]} />

      <div className="mt-5">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
          影视搜索
        </h1>
        <p className="mt-2 text-sm md:text-base text-foreground-500">
          输入影视名称、演员或类型关键词，支持模糊匹配与多维筛选。
        </p>
      </div>

      <form
        onSubmit={submit}
        className="mt-6 flex flex-col sm:flex-row gap-3 max-w-2xl"
      >
        <div className="flex-1 flex items-center gap-2 bg-background-100 border border-background-200 rounded-full px-4 py-2.5 focus-within:border-primary-300 transition-colors">
          <i className="ri-search-line text-foreground-400 text-lg"></i>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="例如：悬疑、林晚舟、长夜将明"
            className="bg-transparent outline-none text-sm w-full text-foreground-900 placeholder:text-foreground-400"
          />
        </div>
        <button
          type="submit"
          className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          搜索
        </button>
      </form>

      {urlKeyword && (
        <p className="mt-4 text-sm text-foreground-600">
          当前关键词：
          <span className="ml-1 px-2 py-0.5 rounded-md bg-accent-100 text-accent-800 font-medium">
            {urlKeyword}
          </span>
        </p>
      )}

      <div className="mt-6 flex flex-col lg:flex-row gap-6 lg:gap-8">
        <div className="flex-1 min-w-0 order-2 lg:order-1">
          <p className="text-sm text-foreground-500 mb-4">
            共找到 <span className="text-foreground-900 font-medium">{result.total}</span> 条结果
          </p>

          {result.items.length > 0 ? (
            <>
              <div className="space-y-3 md:space-y-4">
                {result.items.map((m) => (
                  <Link
                    key={m.id}
                    to={`/detail/${m.id}`}
                    className="group flex gap-4 bg-background-50 rounded-lg border border-background-200 hover:border-primary-200 transition-colors p-3 cursor-pointer"
                  >
                    <div className="w-20 sm:w-24 shrink-0 aspect-[2/3] rounded-md overflow-hidden bg-background-200">
                      <img
                        src={m.cover}
                        alt={`${m.title} 海报`}
                        title={`${m.title} 剧情介绍`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0 py-1">
                      <h2 className="font-heading font-semibold text-base md:text-lg text-foreground-950 group-hover:text-primary-600 transition-colors">
                        <Highlight text={m.title} keyword={urlKeyword} />
                      </h2>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs md:text-sm text-foreground-500">
                        <span>{m.year}</span>
                        <span>{m.type}</span>
                        <span>{m.region}</span>
                        <span className="text-accent-600 font-semibold">
                          {m.rating.toFixed(1)} 分
                        </span>
                      </div>
                      <p className="mt-2 text-xs md:text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                        {m.summary}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
              <Pagination page={result.page} totalPages={result.totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="bg-background-100 rounded-lg border border-background-200 p-6 md:p-8">
              <div className="flex flex-col items-center text-center">
                <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 text-2xl mb-4">
                  <i className="ri-search-line"></i>
                </span>
                <p className="text-foreground-800 font-medium">无相关影视内容，请调整筛选条件</p>
                <p className="mt-1 text-sm text-foreground-500">
                  换个关键词试试，或直接浏览下方热门推荐
                </p>
                <button
                  type="button"
                  onClick={resetAll}
                  className="mt-5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  重置搜索与筛选
                </button>
              </div>

              <div className="mt-8 pt-6 border-t border-background-200">
                <h3 className="font-heading font-semibold text-foreground-900 mb-4 text-sm">
                  为你推荐
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {recommendations.map((m) => (
                    <Link
                      key={m.id}
                      to={`/detail/${m.id}`}
                      className="group flex items-center gap-3 p-2 rounded-lg hover:bg-background-200/60 transition-colors cursor-pointer"
                    >
                      <span className="w-12 h-16 shrink-0 rounded-md overflow-hidden bg-background-200">
                        <img
                          src={m.cover}
                          alt={`${m.title} 海报`}
                          className="w-full h-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-foreground-950 group-hover:text-primary-600 transition-colors truncate">
                          {m.title}
                        </span>
                        <span className="block text-xs text-foreground-500 truncate">
                          {m.year} · {m.genres.join(" / ")}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <aside className="w-full lg:w-72 lg:shrink-0 order-1 lg:order-2">
          <div className="lg:sticky lg:top-20">
            <FilterPanel filters={filters} onChange={updateFilters} onReset={resetAll} />
          </div>
        </aside>
      </div>
    </div>
  );
}